import {
  IPrismicDocument,
  IPrismicEmptyLink,
  IPrismicLink,
} from '@monofront/types';
import {
  FilledContentRelationshipField,
  FilledLinkToWebField,
} from '@prismicio/client';

export const linkResolver = (
  doc: FilledContentRelationshipField,
  trackingId?: string,
): string => {
  if (typeof linkResolvers[doc.type] !== 'function') {
    return '';
  }

  const link = linkResolvers[doc.type](doc);

  if (trackingId) {
    const params = new URLSearchParams(
      link.includes('?') ? link.split('?')[1] : '',
    );

    params.set('trackingId', trackingId);

    return `${link}?${params.toString()}`;
  }

  return linkResolvers[doc.type](doc);
};

export const linkResolverPreview = (
  doc: FilledContentRelationshipField,
  trackingId?: string,
) => {
  const link = linkResolver(doc, trackingId);

  if (link === '') {
    return null;
  }

  return link;
};

interface ILinkResolvers {
  [key: string]: (IDocument) => string;
}

export const isDocumentLink = (
  link: IPrismicLink,
): link is IPrismicDocument => {
  if (link.link_type === 'Document') {
    return true;
  }

  return false;
};

export const isEmptyLink = (link: IPrismicLink): link is IPrismicEmptyLink => {
  if (link.link_type === 'Any') {
    return true;
  }

  return false;
};

const linkResolvers: ILinkResolvers = {
  homeslice: () => '/',
  privatelabelhome: () => '/apps/private-label/home',
  draftproduct: (doc: IPrismicDocument) =>
    `/apps/private-label/draft-product/${doc.uid}`,
  finalproduct: (doc: IPrismicDocument) =>
    `/apps/private-label/result/${doc.uid}`,
  shopaccountlogin: () => '/account/login',
  shopaccountregister: () => '/account/register/flow',
  shopaccountregisterv2: () => '/account/register/flow',
  blogpost: (doc: IPrismicDocument) => `https://blog.lafourche.fr/${doc.uid}`,
  blogcategory: (doc: IPrismicDocument) =>
    `https://blog.lafourche.fr/categorie/${doc.uid}`,
  bloghome: () => 'https://blog.lafourche.fr/',
  homepage: () => '/home',
  page: (doc: IPrismicDocument) => `/pages/${doc.uid}`,
  funnel: (doc: IPrismicDocument) => `/account/${doc.uid}`,
  shopcart: () => `/cart`,
  shopwishlist: () => `/pages/my-lists`,
  shoppurchases: () => `/pages/my-lists?tab=my-purchases`,
  shopaccountaddresses: () => `/account/addresses`,
  shopaccountpersonaldatahandler: () => '/account/personal-data',
  shopaccount: () => `/account`,
  shopaccountmanagesubscription: () => `/pages/manage-my-subscription`,
  shopaccountpaymentmethods: () => `/account/payment-methods`,
  shopaccountorders: () => `/account/orders`,
  shopaccountinformations: () => `/account/informations`,
  shopaccountproductstoreview: () => `/account/review-products`,
  support: () => `https://support.lafourche.fr/hc/fr`,
  productbasket: (doc: IPrismicDocument) => `/product-basket/${doc.uid}`,
  privatelabelresult: () => '/apps/private-label/result',
  referral: () => '/pages/referral',
  reviewproducts: () => '/account/review-products',
  landingpage: (doc: IPrismicDocument) => `/lp/${doc.uid}`,
};

export const getPrismicPreviewUrl = () => {
  if (typeof document === 'undefined') {
    return null;
  }

  let previewId: string | null = null;
  const prismicCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.trim().startsWith('io.prismic.preview='));

  if (prismicCookie) {
    previewId = JSON.parse(
      decodeURIComponent(prismicCookie.split('io.prismic.preview=')[1]),
    )['la-fourche.prismic.io']?.preview;
  }

  return previewId;
};

export const mobileLinkResolver = (link: IPrismicLink) => {
  let mobileLink = '';

  if (link.link_type === 'Web') {
    mobileLink = (link as FilledLinkToWebField).url.replace(
      /(https?:\/\/)(preprod.)?(lafourche)(.fr)/g,
      'lafourche:/',
    );
  } else {
    const documentLink = linkResolver(link as IPrismicDocument);

    mobileLink = documentLink.startsWith('http')
      ? documentLink
      : 'lafourche:/' + documentLink;
  }

  return mobileLink;
};

export const formatPageContents = (group) =>
  group.reduce((group, { slice }) => {
    const sliceList = slice?.data?.body;

    if (sliceList && sliceList.length) {
      sliceList[0].slice_group_uid = slice.uid;

      return [...group, ...sliceList];
    }

    return group;
  }, []);

export const isInPrismicPreview = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const prismicCookie = document.cookie
    .split(';')
    .find((cookie) => cookie.trim().startsWith('io.prismic.preview='));

  if (prismicCookie) {
    return true;
  }

  return false;
};
